import React, { useMemo } from "react";
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowParams,
} from "@mui/x-data-grid";
import { Avatar, Box, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { YouTubeSearchResult } from "../../types/outreach";

type YouTubeSearchRow = {
  id: string;
  channelId: string;
  channelTitle: string;
  thumbnailUrl: string;
  subscriberCount: number;
  viewCount: number;
  videoCount: number;
  isExisting: boolean;
  hasReaction: boolean;
  hasPlaylisting: boolean;
};

type ExistingChannelStatus = {
  reaction: boolean;
  playlisting: boolean;
};

interface YouTubeSearchResultsProps {
  results: YouTubeSearchResult[];
  loading: boolean;
  totalResults: number;
  onSelectChannel: (channelId: string) => void;
  selectedChannelId?: string;
  getExistingChannelStatus: (channelId: string) => ExistingChannelStatus;
}

const formatCount = (count?: number): string => {
  if (typeof count !== "number" || Number.isNaN(count)) {
    return "0";
  }

  if (count >= 1_000_000_000) {
    return `${(count / 1_000_000_000).toFixed(1)}B`;
  }

  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }

  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }

  return count.toLocaleString();
};

const formatVideoCount = (count?: number): string => {
  if (typeof count !== "number" || Number.isNaN(count)) {
    return "0";
  }

  return count.toLocaleString();
};

export const YouTubeSearchResults: React.FC<YouTubeSearchResultsProps> = ({
  results,
  loading,
  totalResults,
  onSelectChannel,
  selectedChannelId,
  getExistingChannelStatus,
}) => {
  const existingChannelStatuses = useMemo(() => {
    const statusMap = new Map<string, ExistingChannelStatus>();

    results.forEach((result) => {
      statusMap.set(
        result.channelId,
        getExistingChannelStatus(result.channelId)
      );
    });

    return statusMap;
  }, [getExistingChannelStatus, results]);

  const columns: GridColDef<YouTubeSearchRow>[] = [
    {
      field: "channelTitle",
      headerName: "Channel Name",
      flex: 0.4,
      minWidth: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<YouTubeSearchRow>) => (
        <Box display="flex" alignItems="center">
          <Avatar
            src={params.row.thumbnailUrl}
            alt={params.value ?? params.row.channelTitle}
            sx={{ width: 40, height: 40, mr: 2 }}
          />
          <Typography variant="body1" noWrap>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "subscriberCount",
      headerName: "Subscribers",
      flex: 0.3,
      minWidth: 120,
      type: "number",
      sortable: false,
      valueFormatter: (value: number | undefined) => formatCount(value),
      sortComparator: (v1: number | undefined, v2: number | undefined) =>
        (v1 ?? 0) - (v2 ?? 0),
    },
    {
      field: "viewCount",
      headerName: "Views",
      flex: 0.3,
      minWidth: 140,
      type: "number",
      sortable: false,
      valueFormatter: (value: number | undefined) => formatCount(value),
      sortComparator: (v1: number | undefined, v2: number | undefined) =>
        (v1 ?? 0) - (v2 ?? 0),
    },
    {
      field: "videoCount",
      headerName: "Videos",
      flex: 0.3,
      minWidth: 100,
      sortable: false,
      valueFormatter: (value: number | undefined) => formatVideoCount(value),
      sortComparator: (v1: number | undefined, v2: number | undefined) =>
        (v1 ?? 0) - (v2 ?? 0),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.35,
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<YouTubeSearchRow>) => {
        if (!params.row.isExisting) {
          return (
            <Typography variant="body2" color="text.secondary">
              Not added
            </Typography>
          );
        }

        return (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {params.row.hasReaction && (
              <Chip
                label="Reaction"
                color="success"
                size="small"
                variant="outlined"
              />
            )}
            {params.row.hasPlaylisting && (
              <Chip
                label="Playlisting"
                color="success"
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
        );
      },
    },
  ];

  const rows: YouTubeSearchRow[] = results.map((result) => {
    const status = existingChannelStatuses.get(result.channelId) ?? {
      reaction: false,
      playlisting: false,
    };

    return {
      id: result.channelId,
      channelId: result.channelId,
      channelTitle: result.channelTitle,
      thumbnailUrl: result.thumbnailUrl,
      subscriberCount: result.subscriberCount ?? 0,
      viewCount: result.viewCount ?? 0,
      videoCount: result.videoCount ?? 0,
      isExisting: status.reaction || status.playlisting,
      hasReaction: status.reaction,
      hasPlaylisting: status.playlisting,
    };
  });

  const handleRowClick = (params: GridRowParams<YouTubeSearchRow>) => {
    onSelectChannel(params.row.channelId);
  };

  const rowSelectionModel = useMemo(() => {
    if (!selectedChannelId) {
      return [];
    }

    const hasSelectedRow = rows.some((row) => row.id === selectedChannelId);

    return hasSelectedRow ? [selectedChannelId] : [];
  }, [rows, selectedChannelId]);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexGrow: 1,
          minHeight: 0,
        }}
        data-testid="skeleton-loading"
      >
        <Skeleton variant="rectangular" sx={{ flexGrow: 1 }} />
      </Box>
    );
  }

  if (results.length === 0 && !loading) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
          flexGrow: 1,
        }}
      >
        <Box sx={{ maxWidth: 400 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {totalResults === 0
              ? "Search for YouTube channels to get started"
              : "No channels found matching your search criteria"}
          </Typography>
          {totalResults > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              Note: YouTube Topic channels (auto-generated channels) are
              automatically filtered from results.
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {results.length}{" "}
          {results.length === 1 ? "channel" : "channels"}
          {totalResults > results.length && (
            <> of {totalResults.toLocaleString()} total</>
          )}
        </Typography>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        onRowClick={handleRowClick}
        rowSelectionModel={rowSelectionModel}
        getRowClassName={(params) =>
          params.row.isExisting ? "existing-channel" : ""
        }
        hideFooterPagination
        hideFooterSelectedRowCount
        sx={{
          height: "100%",
          "& .MuiDataGrid-virtualScroller": {
            overflowY: "auto",
          },
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
            backgroundColor: "action.hover",
          },
          "& .MuiDataGrid-row.existing-channel": {
            backgroundColor: (theme) =>
              alpha(theme.palette.success.light, 0.18),
            "&:hover": {
              backgroundColor: (theme) =>
                alpha(theme.palette.success.light, 0.3),
            },
            "&.Mui-selected": {
              backgroundColor: (theme) =>
                alpha(theme.palette.success.main, 0.25),
              "&:hover": {
                backgroundColor: (theme) =>
                  alpha(theme.palette.success.main, 0.35),
              },
            },
          },
          "& .MuiDataGrid-row.Mui-selected:not(.existing-channel)": {
            backgroundColor: "primary.light",
            "&:hover": {
              backgroundColor: "primary.light",
            },
          },
        }}
      />
    </Box>
  );
};

export default YouTubeSearchResults;
